<template>
  <div class="flex w-full flex-col gap-1 text-xs text-gray-600">
    <div class="flex items-center gap-2">
      <!-- The label points at the slider with `for` instead of wrapping it so
           that the tooltip trigger can sit outside it. Inside, a pointer press
           focuses the trigger and the label's activation behaviour then moves
           focus to the slider, blurring the trigger before the 200ms show
           delay elapses; with no hover to fall back on, a touch user would
           never see the explanation at all.

           It ends after the caption, with the value as a sibling: holding
           both, its visible text reads "Effort: Low" while the slider's own
           aria-label keeps the accessible name "Reasoning effort", which is
           the mismatch an automated WCAG 2.5.3 (Label in Name) pass flags.
           The row's `gap-2` supplies the spacing the nested layout did.

           `Effort:` collapses below sm alongside the row-mate label pattern
           used by the surrounding button row; the label goes with it rather
           than a span inside it, since an emptied label is still a flex item
           and the row's gap would hold its space open. The slider keeps its
           aria-label either way. -->
      <label
        :for="sliderId"
        :class="compactLabel ? 'hidden sm:inline' : undefined"
      >
        Effort:
      </label>
      <span class="font-medium text-gray-900">{{ currentOption.label }}</span>
      <TooltipInfoIcon
        focusable
        tooltip="How much reasoning the model spends before answering. Higher effort means more thorough responses, but takes longer and uses your credits faster."
      />
    </div>
    <input
      :id="sliderId"
      type="range"
      min="0"
      :max="lastIndex"
      step="1"
      :value="currentIndex"
      aria-label="Reasoning effort"
      :aria-valuetext="currentValueText"
      class="w-full accent-purple-600 disabled:cursor-not-allowed disabled:opacity-50"
      :disabled="disabled"
      @input="onSliderInput"
    />
    <!-- Tick labels sit under their slider positions, so the column count
         follows the option count rather than being fixed at four — a model
         that rejects `minimal` offers three. Grid columns come from a style
         binding because a computed `grid-cols-N` class would not survive
         Tailwind's static extraction.

         The endpoint labels carry an inline `($)` / `($$$)` cost hint because
         effort is roughly linear in reasoning tokens (and therefore credits);
         showing the direction at the extremes helps users pick without
         hunting for pricing docs. Middle positions stay unadorned so the hint
         doesn't turn into visual noise. -->
    <div
      class="grid w-full"
      :style="{
        gridTemplateColumns: `repeat(${resolvedOptions.length}, minmax(0, 1fr))`,
      }"
      aria-hidden="true"
      data-testid="effort-ticks"
    >
      <span
        v-for="(option, index) in resolvedOptions"
        :key="option.value"
        class="text-[10px] text-gray-500"
        :class="tickAlignment(index)"
      >
        {{ option.label }}
        <span
          v-if="costHint(index)"
          class="font-semibold tracking-wider text-gray-400"
        >
          {{ costHint(index) }}
        </span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
// One control, two panels. Both AIChatPanel and AIPromptPanel v-model
// `chatEffort` from useAIChatSlideover through this. Slider rather than a
// select because the levels are ordered and the price ↔ effort relationship
// is easier to feel on a linear control than in a dropdown that hides the
// ordering.
import { computed, useId, watch, type PropType } from "vue";
import TooltipInfoIcon from "@/components/Core/TooltipInfoIcon.vue";
import {
  REASONING_EFFORT_OPTIONS,
  type ReasoningEffort,
} from "@/services/ai_agent";

const props = defineProps({
  modelValue: {
    type: String as PropType<ReasoningEffort>,
    required: true,
  },
  // Which levels this model accepts, from the session payload. Defaults to
  // every offered level for the pre-session prompt panel, which has no model
  // yet — the backend clamps an unsupported value there.
  options: {
    type: Array as PropType<readonly ReasoningEffort[] | undefined>,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // Hides the "Effort:" text below sm, where the button row runs out of
  // space; the slider keeps its aria-label either way.
  compactLabel: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  "update:modelValue": (value: ReasoningEffort) => value !== undefined,
});

const sliderId = useId();

// Product order, filtered to what the model takes. An unrecognised value in
// `options` is ignored rather than rendered, and an empty list falls back to
// the full set so the control is never a zero-position slider.
const resolvedOptions = computed(() => {
  const allowed = props.options;
  if (!allowed?.length) return REASONING_EFFORT_OPTIONS;
  const filtered = REASONING_EFFORT_OPTIONS.filter((opt) =>
    allowed.includes(opt.value),
  );
  return filtered.length ? filtered : REASONING_EFFORT_OPTIONS;
});

// The slider is index-based (0..N-1) but the model value is the effort
// string. Fall back to 0 when a caller hands us a value the model does not
// offer — rendering is the right failure mode here, and the watcher below
// then corrects the bound value so the label is not lying about it.
const currentIndex = computed(() => {
  const idx = resolvedOptions.value.findIndex(
    (opt) => opt.value === props.modelValue,
  );
  return idx === -1 ? 0 : idx;
});

const currentOption = computed(() => resolvedOptions.value[currentIndex.value]);

const lastIndex = computed(() => resolvedOptions.value.length - 1);

// A stored preference outlives the session it was chosen in, so switching to
// a model that rejects it (`minimal` on gpt-5.6-luna) would otherwise leave
// the slider showing "Low" while the bound value stayed "minimal" — and the
// backend clamping it to low is invisible from here.
watch(
  [resolvedOptions, () => props.modelValue],
  ([available, value]) => {
    if (!available.some((opt) => opt.value === value)) {
      emit("update:modelValue", available[0].value);
    }
  },
  { immediate: true },
);

function tickAlignment(index: number): string {
  if (index === 0) return "text-left";
  if (index === lastIndex.value) return "text-right";
  return "text-center";
}

// Only the extremes are annotated; `($)` and `($$$)` read as a direction
// rather than a price.
function costHint(index: number): string | undefined {
  if (index === 0) return "($)";
  if (index === lastIndex.value) return "($$$)";
  return undefined;
}

// A range input announces its raw number ("2 of 4") without this, and the
// cost hints are the one thing a sighted user gets that a screen reader would
// not: an aria-label on a bare span is ignored, so the cost direction rides
// on the value text instead.
const currentValueText = computed(() => {
  if (currentIndex.value === 0) {
    return `${currentOption.value.label}, lowest cost`;
  }
  if (currentIndex.value === lastIndex.value) {
    return `${currentOption.value.label}, highest cost`;
  }
  return currentOption.value.label;
});

function onSliderInput(event: Event) {
  const idx = Number((event.target as HTMLInputElement).value);
  const opt = resolvedOptions.value[idx];
  if (opt) {
    emit("update:modelValue", opt.value);
  }
}
</script>
